import { ErrorHandlerCaller as EHC, DEFAULTS_HANDLERS } from "@/utils/errors"
import { HTTP, BACKEND_API_URL } from "@/utils/http-common"

export default {
  async getCards(vm, params = {}) {
    try {
      const response = await HTTP.get('/activity/cards/physical', { params })
      return response.data
    } catch (e) {
      new EHC(DEFAULTS_HANDLERS).intercept(e)
      throw e
    }
  },

  /**
   * Statistiques des cartes par statut
   */
  async getStats(vm) {
    try {
      const response = await HTTP.get('/activity/cards/physical/stats')
      return response.data
    } catch (e) {
      this.handleError(vm, e)
      return {
        total: 0,
        available: 0,
        assigned: 0,
        blocked: 0,
        lost: 0,
        active: 0,
      }
    }
  },

  /**
   * Assigner une carte à un utilisateur
   */
  async assignCard(vm, cardId, userId) {
    try {
      const response = await HTTP.post(`/activity/cards/physical/${cardId}/assign`, {
        user_id: userId
      })
      return response.data
    } catch (e) {
      this.handleError(vm, e)
      throw e
    }
  },

  /**
   * Désactiver une carte
   */
  async deactivateCard(vm, cardId) {
    try {
      const response = await HTTP.post(`/activity/cards/physical/${cardId}/deactivate`)
      return response.data
    } catch (e) {
      this.handleError(vm, e)
      throw e
    }
  },

  /**
   * Bloquer une carte
   */
  async blockCard(vm, cardId) {
    try {
      const response = await HTTP.post(`/activity/cards/physical/${cardId}/block`)
      return response.data
    } catch (e) {
      this.handleError(vm, e)
      throw e
    }
  },

  /**
   * Marquer une carte comme perdue
   */
  async markLost(vm, cardId) {
    try {
      const response = await HTTP.post(`/activity/cards/physical/${cardId}/mark-lost`)
      return response.data
    } catch (e) {
      this.handleError(vm, e)
      throw e
    }
  },

  /**
   * Générer plusieurs cartes physiques
   */
  async generateCards(vm, payload) {
    try {
      const response = await HTTP.post('/activity/cards/physical/generate', payload)
      return response.data
    } catch (e) {
      this.handleError(vm, e)
      throw e
    }
  }
  ,

  /**
   * Générer un PDF avec QR codes pour les cartes sélectionnées
   */
  /**
   * Générer un PDF avec QR codes pour les cartes sélectionnées
   * Utilise XMLHttpRequest pour garantir le responseType blob
   * Inspiré de la méthode getBadge qui fonctionne
   */
  generatePDF(vm, cardIds) {
    return new Promise((resolve, reject) => {
      const url = `${BACKEND_API_URL}/activity/cards/physical/generate-pdf`
      const xhr = new XMLHttpRequest()
      
      xhr.open("POST", url, true)
      
      // Headers
      const token = vm.$store.state.app.user?.key || localStorage.getItem('token') || sessionStorage.getItem('token')
      if (token) {
        xhr.setRequestHeader("authorization", "Token " + token)
      }
      xhr.setRequestHeader("Content-Type", "application/json")
      xhr.setRequestHeader("Accept", "*/*")
      
      // Timezone si disponible
      const tzName = vm.$store.state.app.user?.tzName
      if (tzName) {
        xhr.setRequestHeader("X-Timezone-Name", tzName)
      }
      
      // IMPORTANT: responseType blob pour recevoir le fichier binaire
      xhr.responseType = "blob"
      
      xhr.onload = function () {
        if (xhr.status === 200) {
          // Vérifier que c'est bien un blob
          if (xhr.response instanceof Blob) {
            console.log('PDF Blob reçu:', {
              type: xhr.response.type,
              size: xhr.response.size
            })
            resolve(xhr.response)
          } else {
            console.error('Response is not a Blob:', xhr.response)
            reject(new Error('La réponse n\'est pas un fichier PDF valide'))
          }
        } else {
          console.error('HTTP error:', xhr.status, xhr.statusText)
          reject(new Error(`Erreur serveur: ${xhr.status} ${xhr.statusText}`))
        }
      }
      
      xhr.onerror = function () {
        console.error('XHR error:', xhr)
        reject(new Error('Erreur réseau lors de la génération du PDF'))
      }
      
      // Envoyer les données
      const payload = JSON.stringify({ card_ids: cardIds })
      xhr.send(payload)
    })
  },

  /**
   * Déclenche l'export asynchrone de toutes les cartes actives.
   * Le PDF sera généré en arrière-plan et envoyé par email à l'utilisateur.
   * 
   * @param {Object} vm - Instance Vue
   * @param {Object} filters - Filtres optionnels { status: 'assigned' }
   * @returns {Promise<Object>} - { success, task_id, card_count, email, message }
   * 
   * @example
   * const result = await PhysicalCardService.exportAllActiveCards(this, { status: 'assigned' })
   * // result = { success: true, task_id: "abc123", card_count: 150, email: "user@example.com" }
   */
  async exportAllActiveCards(vm, filters = {}) {
    try {
      const response = await HTTP.post('/activity/cards/physical/export-all', filters)
      return response.data
    } catch (e) {
      // Extraire le message d'erreur de la réponse
      const errorMessage = e.response?.data?.error || 
                          e.response?.data?.detail || 
                          'Erreur lors du lancement de l\'export'
      
      // Notifier l'utilisateur
      if (vm.$store) {
        vm.$store.commit("app/message", {
          msg: errorMessage,
          color: "error"
        })
      }
      
      throw new Error(errorMessage)
    }
  },

  /**
   * Vérifie le statut d'une tâche d'export Celery.
   * 
   * @param {Object} vm - Instance Vue
   * @param {string} taskId - ID de la tâche retourné par exportAllActiveCards
   * @returns {Promise<Object>} - { task_id, status, ready, result?, error? }
   * 
   * Statuts possibles:
   * - PENDING: En attente
   * - STARTED: En cours
   * - SUCCESS: Terminé avec succès
   * - FAILURE: Échec
   * 
   * @example
   * const status = await PhysicalCardService.getExportStatus(this, 'abc123')
   * if (status.ready && status.result?.success) {
   *   console.log('Export terminé!')
   * }
   */
  async getExportStatus(vm, taskId) {
    try {
      const response = await HTTP.get(`/activity/cards/physical/export-status/${taskId}`)
      return response.data
    } catch (e) {
      console.error('Erreur vérification statut export:', e)
      throw e
    }
  },

  /**
   * Polling automatique du statut d'export.
   * Vérifie périodiquement jusqu'à ce que la tâche soit terminée.
   * 
   * @param {Object} vm - Instance Vue
   * @param {string} taskId - ID de la tâche
   * @param {Function} onUpdate - Callback appelé à chaque mise à jour
   * @param {number} interval - Intervalle en ms (défaut: 5000)
   * @returns {Function} - Fonction pour arrêter le polling
   * 
   * @example
   * const stopPolling = PhysicalCardService.pollExportStatus(
   *   this,
   *   taskId,
   *   (status) => {
   *     console.log('Statut:', status.status)
   *     if (status.ready) {
   *       this.$store.commit("app/message", {
   *         msg: "Export terminé ! Vérifiez votre email.",
   *         color: "success"
   *       })
   *     }
   *   },
   *   5000
   * )
   * 
   * // Pour arrêter manuellement:
   * stopPolling()
   */
  pollExportStatus(vm, taskId, onUpdate, interval = 5000) {
    let timerId = null
    let stopped = false
    
    const check = async () => {
      if (stopped) return
      
      try {
        const status = await this.getExportStatus(vm, taskId)
        
        if (onUpdate && typeof onUpdate === 'function') {
          onUpdate(status)
        }
        
        // Arrêter si la tâche est terminée
        if (status.ready) {
          stopped = true
          return
        }
        
        // Programmer la prochaine vérification
        timerId = setTimeout(check, interval)
      } catch (error) {
        console.error('Erreur polling export:', error)
        
        if (onUpdate && typeof onUpdate === 'function') {
          onUpdate({ error: error.message, status: 'ERROR' })
        }
        
        // Continuer le polling malgré l'erreur (peut être temporaire)
        timerId = setTimeout(check, interval)
      }
    }
    
    // Démarrer le polling
    check()
    
    // Retourner une fonction pour arrêter le polling
    return () => {
      stopped = true
      if (timerId) {
        clearTimeout(timerId)
        timerId = null
      }
    }
  },

  /**
   * Générer un PDF et retourner directement l'URL blob
   * Comme getBadge dans l'exemple
   */
  generatePDFUrl(vm, cardIds) {
    return new Promise((resolve, reject) => {
      const url = `${BACKEND_API_URL}/activity/cards/physical/generate-pdf`
      const xhr = new XMLHttpRequest()
      
      xhr.open("POST", url, true)
      
      const token = vm.$store.state.app.user?.key || localStorage.getItem('token') || sessionStorage.getItem('token')
      if (token) {
        xhr.setRequestHeader("authorization", "Token " + token)
      }
      xhr.setRequestHeader("Content-Type", "application/json")
      xhr.setRequestHeader("Accept", "*/*")
      
      const tzName = vm.$store.state.app.user?.tzName
      if (tzName) {
        xhr.setRequestHeader("X-Timezone-Name", tzName)
      }
      
      xhr.responseType = "blob"
      
      xhr.onload = function () {
        if (xhr.status === 200) {
          // Créer l'URL blob directement
          const blobUrl = URL.createObjectURL(xhr.response)
          resolve(blobUrl)
        } else {
          reject(xhr)
        }
      }
      
      xhr.onerror = function () {
        reject(xhr)
      }
      
      const payload = JSON.stringify({ card_ids: cardIds })
      xhr.send(payload)
    })
  }
}
